import { type ReactNode } from 'react'
import { Avatar as DsAvatar, Menu, Skeleton } from '@systemeio/components'
import { ChevronDownIcon } from '@systemeio/icons'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { twMerge } from 'tailwind-merge'
import { addPublicCustomClassNamePrefix } from 'modules/course/utils/add-class-prefix'
import useUser, { isAuthorizedUser } from '../hooks/use-user'

function Avatar({
  popupClassName,
  linkClassName,
}: {
  popupClassName?: string
  linkClassName?: string
}): ReactNode | null {
  const router = useRouter()
  const { user, loading, showChangePasswordPage } = useUser()
  const { t } = useTranslation()

  if (user && !isAuthorizedUser(user)) {
    return null
  }

  if (loading || !user) {
    return (
      <Skeleton
        className={twMerge(
          'size-10 rounded-full',
          addPublicCustomClassNamePrefix('avatar-skeleton'),
        )}
      />
    )
  }

  return (
    <div className={twMerge('shrink-0', addPublicCustomClassNamePrefix('avatar'))}>
      <Menu
        className={popupClassName}
        trigger={[
          <span key="avatar" data-testid="header-avatar-icon" className="inline-flex shrink-0">
            <DsAvatar
              src={user.avatarUrl}
              firstName={user.firstName}
              lastName={user.lastName}
              alt={`${user.firstName} ${user.lastName}`}
              size="lg"
            />
          </span>,
          <ChevronDownIcon key="chevron" className="size-3 shrink-0" aria-hidden="true" />,
        ]}
        triggerProps={{
          className: twMerge('flex flex-row items-center gap-2.5', linkClassName),
        }}
        items={[
          ...(!user.workspaceOwner
            ? [
                {
                  id: 'settings',
                  label: t('user.profile.menu.settings'),
                  className: linkClassName,
                  onSelect: () => {
                    void router.push('/profile/user-settings')
                  },
                },
              ]
            : []),
          ...(showChangePasswordPage
            ? [
                {
                  id: 'password',
                  label: t('user.profile.menu.change_password'),
                  className: linkClassName,
                  onSelect: () => {
                    void router.push('/profile/change-password')
                  },
                },
              ]
            : []),
          ...(user.impersonator
            ? [
                {
                  id: 'exit-impersonation',
                  label: t('header.exit_impersonation'),
                  className: linkClassName,
                  onSelect: () => {
                    globalThis.location.href = '/dashboard/back/entry?pretend_this_user=_exit'
                  },
                },
              ]
            : []),
          {
            id: 'logout',
            label: t('user.profile.menu.logout'),
            className: linkClassName,
            onSelect: () => {
              globalThis.location.href = '/dashboard/logout'
            },
          },
        ]}
      />
    </div>
  )
}

export default Avatar
